import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { ComponentPropsWithoutRef } from 'react';
import { ID_NEW } from '../../../../../domain/model/category/ICategoryRepository';
import { WebCategory } from '../../../../../domain/model/category/WebCategory';
import {
  actions as feedAction,
  convertCacheKey,
  type Action as FeedAction
} from '../../../../../public/template/pc/top/store/newsFeed';
import Component, { ITabCategory } from '../components/js/NewsFeedLocalTab';
import { ITopStates } from '../store';
import { actions as tabAction, IRegion, type Action as TabAction, IPrefectureStore } from '../store/newsFeedLocalTab';

const mapStateToProps = (state: ITopStates) => {
  const { currentRegion, currentPrefecture, regions, prefectureStore } = state.newsFeedLocalTab;

  // 親タブ要素を選択状態にする
  const regionElements: ITabCategory[] = regions.map((region, index): ITabCategory => {
    return {
      id: region.id,
      name: region.name,
      // currentRegionが存在しない場合は先頭を選択状態とする
      isCurrent: currentRegion ? region.id === currentRegion.id : index === 0
    };
  });

  // 子タブ要素を選択状態にする
  const prefectureElements: ITabCategory[] = currentRegion
    ? currentRegion.prefectures.map((prefecture, index): ITabCategory => {
        return {
          ...prefecture,
          // currentPrefectureが存在しない場合は先頭を選択状態とする
          isCurrent: currentPrefecture ? prefecture.id === currentPrefecture.id : index === 0
        };
      })
    : [];

  return {
    regions: regionElements,
    currentRegionId: currentRegion ? currentRegion.id : undefined,
    currentPrefectureId: currentPrefecture ? currentPrefecture.id : undefined,
    prefecturesInRegion: prefectureElements,
    // mergePropsで使う
    allRegion: regions,
    prefectureStore,
    stateCache: state.newsFeed.stateCache,
    userToken: state.userToken
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<ITopStates, void, TabAction | FeedAction>) => ({
  changeParentTab: (id: string) => {
    // タブを切り替える
    dispatch(tabAction.changeParentTab(id));
  },
  changeTab: (regionId: string, prefectureId: string) => {
    // タブ切り替え
    dispatch(tabAction.changeTab(regionId, prefectureId));
  },
  fetchFeed: (prefectureId: string, userToken: string | null) => {
    // タイムライン取得
    dispatch(feedAction.fetchFeed(WebCategory.LOCAL, prefectureId, userToken));
  },
  reflectStorage: () => {
    // session storageの情報を反映する
    dispatch(feedAction.reflectSessionStorage);
    // localstorageの状態を反映
    dispatch(tabAction.reflectLocalStorage);
  },
  updateSessionStorage(subCategoryId: string) {
    dispatch(feedAction.updateSessionStorage(WebCategory.LOCAL, subCategoryId));
  }
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type OwnProps = Omit<ComponentPropsWithoutRef<typeof Component>, keyof StateProps | keyof DispatchProps>;

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    changeTab: (regionId: string, prefectureId: string) => {
      dispatchProps.changeTab(regionId, prefectureId);

      const stateCache = stateProps.stateCache;
      const cacheKey = convertCacheKey(WebCategory.LOCAL, prefectureId);

      if (stateCache[cacheKey]) {
        // cacheがあればsession storageの更新のみ
        dispatchProps.updateSessionStorage(prefectureId);
      } else {
        // 切替時に以前の状態がない場合、都道府県の記事を取得する
        dispatchProps.fetchFeed(prefectureId, stateProps.userToken.value);
      }
    },
    changeParentTab: (regionId: string) => {
      const { stateCache, prefectureStore, allRegion } = stateProps;
      // regionIdがID_NEWであればそのまま
      // それ以外は、selectNextIdメソッドに従ったidを使う
      const nextId = regionId === ID_NEW ? ID_NEW : selectNextId(regionId, prefectureStore, allRegion);

      if (nextId) {
        dispatchProps.changeParentTab(regionId);
        const cacheKey = convertCacheKey(WebCategory.LOCAL, nextId);

        if (stateCache[cacheKey]) {
          // cacheがあればsession storageの更新のみ
          dispatchProps.updateSessionStorage(nextId);
        } else {
          // 切替時に以前の状態がない場合、都道府県の記事を取得する
          dispatchProps.fetchFeed(nextId, stateProps.userToken.value);
        }
      }
    }
  };
};

/**
 * changeParentTab実行時に次に選択されるidを判定して返す
 *
 * @param regionId
 * @param selectedPrefectures
 * @param allRegion
 * @returns 条件に合致したprefectureIdを返す
 */
function selectNextId(
  regionId: string,
  selectedPrefectures: IPrefectureStore | undefined,
  allRegion: IRegion[]
): string | undefined {
  if (selectedPrefectures?.[regionId]) {
    // 指定した都道府県がすでに選択されていれば、それに紐づくidを返す
    return selectedPrefectures[regionId];
  }
  // 値が存在しない場合は選択した地方の都道府県の中から先頭のものを利用する
  const selectedRegion = allRegion.find(region => region.id === regionId);
  const firstPrefecture = selectedRegion?.prefectures ? selectedRegion.prefectures[0] : undefined;

  return firstPrefecture ? firstPrefecture.id : undefined;
}

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(Component);
